import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface BakeryPartnerRow {
  id: string;
  vendor_name: string;
  is_active: boolean;
  profile_id: string;
  contact_email: string | null;
  abn: string | null;
  website: string | null;
  instagram: string | null;
  product_types: string[] | null;
  dietary_options: string[] | null;
  delivery_suburbs: string | null;
}

export interface BakeryOrderRow {
  id: string;
  reference: string;
  team_member_name: string;
  celebration_type: string;
  cake_type: string | null;
  cake_size: string | null;
  // MS216: vendor_category drives the category-aware label for the
  // `baking` status (Baking / Packing / Tying / Preparing) on the
  // partner portal — same MS214 pattern applied to vendor-side UI.
  vendor_category: string | null;
  dietary_needs: string[] | null;
  special_instructions: string | null;
  delivery_address: string | null;
  delivery_date: string | null;
  delivery_time: string | null;
  budget_cents: number;
  status: string;
  notes: string | null;
  created_at: string | null;
}

export interface BakeryNotice {
  id: string;
  title: string;
  message: string;
  priority: string;
}

export interface BakeryNotificationPrefs {
  email_new_orders: boolean;
  email_order_updates: boolean;
  email_notices: boolean;
}

export interface BakeryProduct {
  id: string;
  name: string;
  description: string | null;
  price: number;
  category: string;
  dietary_tags: string[] | null;
  serves_min: number | null;
  serves_max: number | null;
  is_available: boolean | null;
  sort_order: number | null;
  // MS317: lead time the vendor needs to prepare this SKU. Drives
  // the OrderConfirmSheet earliest-delivery gate (MS312) and the
  // server-side gate (MS313). Browse "How soon?" filter (MS314)
  // reads this too. Null = treat as same-day (no constraint
  // beyond the today+1 floor). The SELECT * in useBakeryProducts
  // already pulled this column from the row; typing it here so
  // vendors can edit it via BakeryCatalogue.
  lead_time_days: number | null;
}

/**
 * Product catalogue for a vendor partner. Ordered by sort_order.
 * Mutations (insert/update/delete/availability toggle) should
 * invalidate ["vendor_products", partnerId] to refresh the list.
 */
export function useBakeryProducts(partnerId: string | null | undefined) {
  return useQuery({
    queryKey: ["vendor_products", partnerId],
    enabled: Boolean(partnerId),
    queryFn: async (): Promise<BakeryProduct[]> => {
      if (!partnerId) return [];
      // Filter out soft-deleted rows. Hard delete used to be the
      // remove-product path; replaced with deleted_at timestamping
      // (migration 20260501060000) so order history that joins back
      // to product names doesn't lose its target row.
      const { data, error } = await supabase
        .from("vendor_products")
        .select("*")
        .eq("vendor_partner_id", partnerId)
        .is("deleted_at", null)
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return (data as unknown as BakeryProduct[] | null) ?? [];
    },
  });
}

export interface BakeryBlockedDate {
  id: string;
  unavailable_date: string;
  reason: string | null;
}

/**
 * Blocked dates where the bakery can't accept orders. Used by
 * /bakery/availability. Mutations should invalidate
 * ["bakery_availability", partnerId] after insert/delete.
 */
export function useBakeryBlockedDates(partnerId: string | null | undefined) {
  return useQuery({
    queryKey: ["bakery_availability", partnerId],
    enabled: Boolean(partnerId),
    queryFn: async (): Promise<BakeryBlockedDate[]> => {
      if (!partnerId) return [];
      const { data, error } = await supabase
        .from("bakery_availability")
        .select("id, unavailable_date, reason")
        .eq("bakery_partner_id", partnerId)
        .order("unavailable_date", { ascending: true });
      if (error) throw error;
      return (data as BakeryBlockedDate[] | null) ?? [];
    },
  });
}

/**
 * Notification preferences for a bakery partner. Returns null when no row
 * exists yet; the UI should render defaults in that case and upsert on
 * first toggle.
 */
export function useBakeryNotificationPrefs(partnerId: string | null | undefined) {
  return useQuery({
    queryKey: ["bakery_notification_prefs", partnerId],
    enabled: Boolean(partnerId),
    queryFn: async (): Promise<BakeryNotificationPrefs | null> => {
      if (!partnerId) return null;
      const { data, error } = await supabase
        .from("bakery_notification_prefs")
        .select("email_new_orders, email_order_updates, email_notices")
        .eq("bakery_partner_id", partnerId)
        .maybeSingle();
      if (error) throw error;
      return (data as BakeryNotificationPrefs | null) ?? null;
    },
  });
}

/**
 * Current user's vendor_partners row. Shared by BakeryProtectedRoute (for
 * the is_active gate), BakeryLayout (for the header name), BakeryDashboard
 * (for the greeting), and anywhere else in the portal that needs the
 * partner context. One query, one cache entry, every surface stays in
 * sync.
 */
export function useBakeryPartner() {
  const { user } = useAuth();
  const userId = user?.id;

  return useQuery({
    queryKey: ["vendor_partners", "self", userId],
    enabled: Boolean(userId),
    queryFn: async (): Promise<BakeryPartnerRow | null> => {
      if (!userId) return null;
      const { data, error } = await supabase
        .from("vendor_partners")
        .select("*")
        .eq("profile_id", userId)
        .maybeSingle();
      if (error) throw error;
      return (data as unknown as BakeryPartnerRow | null) ?? null;
    },
  });
}

/**
 * Full orders list for the bakery portal. MS216 added a vendor_partner_id
 * FK to orders + RLS policies scoping bakery reads to matching rows, so
 * the select-all here is now correctly auto-filtered by RLS. We also
 * pass an explicit .eq filter once we know the partnerId — same set of
 * rows, but the query uses the idx_orders_vendor_partner_id index and
 * doesn't rely solely on RLS for correctness.
 */
export function useBakeryOrders() {
  const { user } = useAuth();
  const userId = user?.id;
  return useQuery({
    queryKey: ["bakery", "orders", userId],
    staleTime: 15_000,
    // MS343: poll every 60s while the bakery dashboard / orders page is
    // open so a vendor leaving the tab in the background sees new
    // pending orders surface without a manual refresh. Pre-MS343 they
    // had to alt-tab or hit refresh to spot a new order (email
    // notification still fires, but vendors who spend their day in the
    // tab were missing the visual). 60s = perceptual fresh-feeling
    // without hammering the DB; small vendor count makes the load
    // negligible. refetchOnWindowFocus still picks up changes when they
    // tab back in (react-query default).
    refetchInterval: 60_000,
    enabled: Boolean(userId),
    queryFn: async (): Promise<BakeryOrderRow[]> => {
      if (!userId) return [];
      // Resolve the bakery user's vendor_partner_id. Without it the
      // RLS policy returns zero rows, so we'd waste a round-trip
      // querying orders.
      const { data: vp, error: vpErr } = await supabase
        .from("vendor_partners")
        .select("id")
        .eq("profile_id", userId)
        .maybeSingle();
      if (vpErr) throw vpErr;
      if (!vp?.id) return [];

      const { data, error } = await supabase
        .from("orders")
        .select("*")
        .order("created_at", { ascending: false })
        // MS397: cap at 200 — even a high-volume bakery partner will rarely
        // need to scroll further than the last 200 orders in the dashboard.
        .limit(200);
      if (error) throw error;
      return (data as unknown as BakeryOrderRow[] | null) ?? [];
    },
  });
}

/**
 * Active admin-issued notices for the bakery dashboard banner. Excludes
 * anything with an explicit expires_at in the past. Top 3 by creation.
 */
export function useBakeryActiveNotices() {
  return useQuery({
    queryKey: ["bakery_notices", "active"],
    staleTime: 60_000,
    queryFn: async (): Promise<BakeryNotice[]> => {
      const { data, error } = await supabase
        .from("bakery_notices")
        .select("id, title, message, priority")
        .or(`expires_at.is.null,expires_at.gt.${new Date().toISOString()}`)
        .order("created_at", { ascending: false })
        .limit(3);
      if (error) throw error;
      return (data as BakeryNotice[] | null) ?? [];
    },
  });
}
